package com.anjuros.website.redesign.components

import androidx.compose.runtime.Composable
import com.anjuros.website.components.language.LocalText
import com.anjuros.website.redesign.styles.RdTableFrameStyle
import com.anjuros.website.redesign.styles.RdTableHintStyle
import com.anjuros.website.redesign.styles.css
import com.anjuros.website.redesign.util.RdText
import com.varabyte.kobweb.compose.ui.Modifier
import com.varabyte.kobweb.compose.ui.attrsModifier
import com.varabyte.kobweb.compose.ui.toAttrs
import com.varabyte.kobweb.silk.style.toModifier
import kotlinx.browser.window
import org.jetbrains.compose.web.dom.Div
import org.jetbrains.compose.web.dom.Span
import org.jetbrains.compose.web.dom.Text
import org.w3c.dom.HTMLElement
import org.w3c.dom.events.Event

/**
 * A table box that scrolls sideways when the table is wider than the screen. While it does, a "swipe" hint shows
 * above it, the right edge fades with an arrow and the first column stays put (see [RdTableFrameStyle]).
 * [boxModifier] styles the scrolling box itself (border, corners); [radiusPx] must match its corner radius.
 * [frameModifier] can add rules that only apply while the table scrolls, keyed on the frame's data-scroll.
 */
@Composable
fun RdScrollTable(boxModifier: Modifier, radiusPx: Int, frameModifier: Modifier = Modifier, table: @Composable () -> Unit) {
    Div(RdTableFrameStyle.toModifier().then(frameModifier).css("--rd-table-radius" to "${radiusPx}px").toAttrs()) {
        Span(RdTableHintStyle.toModifier().attrsModifier { attr("aria-hidden", "true") }.toAttrs()) {
            Text(LocalText(RdText.TABLE_SWIPE))
        }
        // Holds the fade over the table's right edge; it can't go on the scrolling box itself or it would scroll away.
        Div {
            Div(boxModifier.toAttrs {
                ref { box ->
                    val dispose = watchTableScroll(box)
                    onDispose { dispose() }
                }
            }) { table() }
        }
    }
}

/**
 * Marks the table's frame with data-scroll: "none" when the whole table fits, otherwise "start", "middle" or "end"
 * depending on how far it has been scrolled, so the styles can fade the right edge and pin the first column.
 */
private fun watchTableScroll(box: HTMLElement): () -> Unit {
    val frame = box.parentElement?.parentElement ?: return {}
    val update: (Event?) -> Unit = {
        val hidden = box.scrollWidth - box.clientWidth
        val state = when {
            hidden <= 1 -> "none"
            box.scrollLeft <= 1 -> "start"
            box.scrollLeft >= hidden - 1 -> "end"
            else -> "middle"
        }
        frame.setAttribute("data-scroll", state)
    }
    update(null)
    box.addEventListener("scroll", update)
    window.addEventListener("resize", update)
    return {
        box.removeEventListener("scroll", update)
        window.removeEventListener("resize", update)
    }
}
